import React from 'react';
import './UpcomingPaymentsChart.css';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function periodKeyOf(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${date.getFullYear()}-${month}`;
}

function dateFromPeriodKey(key) {
    const match = String(key || '').match(/^(\d{4})-(\d{2})/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, 1);
}

function dateFromPeriodLabel(label) {
    const match = String(label || '').match(/([A-Za-zÁÉÍÓÚáéíóúÑñ]+)\s+(\d{4})/);
    if (!match) return null;

    const index = MONTH_NAMES.findIndex((name) => name.toLowerCase() === match[1].toLowerCase());
    if (index < 0) return null;

    return new Date(Number(match[2]), index, 1);
}

function obligationDate(item) {
    return dateFromPeriodKey(item?.period_key) || dateFromPeriodLabel(item?.period);
}

function UpcomingPaymentsChart({ data = [], referenceDate }) {
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatCompactCurrency = (amount) => {
        if (amount === 0) return '$0';
        
        const absAmount = Math.abs(amount);
        
        // Para millones (1.000.000 o más)
        if (absAmount >= 1000000) {
            const millions = amount / 1000000;
            return `$${millions.toFixed(1)}M`;
        }
        
        // Para miles (1.000 o más)
        if (absAmount >= 1000) {
            const thousands = amount / 1000;
            // Si es un número redondo (ej: 16000), no mostrar decimales
            if (thousands % 1 === 0) {
                return `$${Math.round(thousands)}k`;
            }
            return `$${thousands.toFixed(1)}k`;
        }
        
        // Para números menores a 1000, mostrar completo
        return `$${amount}`;
    };

    const today = referenceDate instanceof Date ? referenceDate : new Date();
    const currentStart = new Date(today.getFullYear(), today.getMonth(), 1);

    // Current month plus the next three. Unpaid months before today are prepended
    // (two months at most) so an overdue installment is not drawn as already paid.
    const generateMonthsArray = () => {
        let windowStart = new Date(currentStart);
        const overdueDates = data
            .filter((item) => item?.bucket === 'overdue' && Number(item?.amount) > 0)
            .map(obligationDate)
            .filter((date) => date && date < currentStart)
            .sort((left, right) => left - right);

        if (overdueDates.length > 0) {
            const earliest = overdueDates[0];
            const minStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 2, 1);
            windowStart = earliest < minStart ? minStart : earliest;
        }

        const windowEnd = new Date(currentStart.getFullYear(), currentStart.getMonth() + 3, 1);
        const months = [];

        for (
            let cursor = new Date(windowStart);
            cursor <= windowEnd;
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)
        ) {
            const key = periodKeyOf(cursor);
            const monthData = data.find((item) => {
                const itemDate = obligationDate(item);
                return itemDate && periodKeyOf(itemDate) === key;
            });

            months.push({
                date: new Date(cursor),
                period: monthData?.period || `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`,
                amount: Number(monthData?.amount) || 0,
                due_date: monthData?.due_date || null,
                bucket: monthData?.bucket || null,
                isCurrentMonth: key === periodKeyOf(currentStart),
            });
        }

        return months;
    };

    // Get month abbreviation from period string
    const getMonthAbbr = (periodLabel) => {
        const months = {
            'Enero': 'ene', 'Febrero': 'feb', 'Marzo': 'mar', 'Abril': 'abr',
            'Mayo': 'may', 'Junio': 'jun', 'Julio': 'jul', 'Agosto': 'ago',
            'Septiembre': 'sep', 'Octubre': 'oct', 'Noviembre': 'nov', 'Diciembre': 'dic'
        };

        for (const [full, abbr] of Object.entries(months)) {
            if (periodLabel.includes(full)) {
                return abbr;
            }
        }
        return periodLabel.substring(0, 3).toLowerCase();
    };

    // Format due date for display
    const formatDueDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString + 'T00:00:00');
        const day = date.getDate();
        const monthAbbr = date.toLocaleDateString('es-AR', { month: 'short' });
        return `${day}/${monthAbbr}`;
    };

    const monthsData = generateMonthsArray();
    const currentMonth = monthsData.find((month) => month.isCurrentMonth);
    const maxAmount = Math.max(...monthsData.map(m => m.amount), 1); // At least 1 to avoid division by zero

    const barColor = (month) => {
        if (month.bucket === 'overdue') return '#f59e0b';
        if (month.isCurrentMonth) return 'var(--bs-primary)';
        return '#90caf9';
    };

    const labelColor = (month) => {
        if (month.bucket === 'overdue') return '#b45309';
        if (month.isCurrentMonth) return 'var(--bs-primary)';
        return '#9e9e9e';
    };

    return (
        <div className="upcoming-payments-chart">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h6 className="mb-1">Próximos meses</h6>
                </div>
            </div>

            <div className="chart-months d-flex justify-content-around gap-3 mb-2">
                {monthsData.map((month, index) => (
                    <div 
                        key={index} 
                        className={`month-label text-center ${month.isCurrentMonth ? 'fw-semibold' : ''}`}
                        style={{ 
                            color: labelColor(month),
                            flex: '1 1 0',
                            minWidth: 0
                        }}
                    >
                        {getMonthAbbr(month.period)}
                    </div>
                ))}
            </div>

            <div className="chart-container d-flex align-items-end justify-content-around gap-3 mb-3" style={{ height: '120px' }}>
                {monthsData.map((month, index) => {
                    const amountPercentage = (month.amount / maxAmount) * 100;
                    const hasAmount = month.amount > 0;
                    
                    return (
                        <div 
                            key={index} 
                            className="chart-bar-wrapper"
                            style={{ height: '100%', flex: '1 1 0', minWidth: 0, maxWidth: '72px' }}
                        >
                            {/* Base gray bar (always 100% visible) */}
                            <div 
                                className="chart-bar-base"
                                style={{ 
                                    height: '100%',
                                    backgroundColor: '#e0e0e0',
                                    position: 'relative',
                                    borderRadius: '12px 12px 0 0'
                                }}
                            >
                                {/* Blue bar (amount) stacked from bottom */}
                                {hasAmount && (
                                    <div 
                                        className="chart-bar-amount"
                                        style={{ 
                                            height: `${amountPercentage}%`,
                                            backgroundColor: barColor(month),
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            borderRadius: amountPercentage > 95 ? '12px 12px 0 0' : '0'
                                        }}
                                    >
                                        <div 
                                            className="bar-dot" 
                                            style={{ 
                                                backgroundColor: barColor(month)
                                            }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="chart-amounts d-flex justify-content-around gap-3">
                {monthsData.map((month, index) => (
                    <div 
                        key={index} 
                        className={`amount-label text-center ${month.isCurrentMonth && month.amount > 0 ? 'fw-bold' : ''}`}
                        style={{ 
                            color: labelColor(month),
                            flex: '1 1 0',
                            minWidth: 0,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <div className="fs-6">
                            {formatCompactCurrency(month.amount)}
                        </div>
                    </div>
                ))}
            </div>

            {currentMonth?.due_date && (
                <div className="text-muted small text-center mt-2">
                    Cierra el {formatDueDate(currentMonth.due_date)}
                </div>
            )}
        </div>
    );
}

export default UpcomingPaymentsChart;
